/**
 * Event list — the member event index: a poster-tile grid mirroring the
 * storefront landing page's visual language (sold-out curtain precedent),
 * adapted for admin lifecycle status instead of inventory. The create
 * action rides the trailing end of the count line; a tile click opens
 * `event-form` in edit mode via `show()`, the same overlay the count-line
 * action opens in create mode. Loading/error/denied/empty/populated mirrors
 * members.view.ts's five-frame state machine. The tile itself is
 * `event-tile.view.ts` — shared with event-form.view.ts's live preview pane
 * so the two never render a diverging card.
 *
 * Each populated tile fills `event-tile`'s own `footer` slot with
 * `event-sale-progress` over that event's figures, read straight off
 * `eventSaleStats` (stats.state.ts) by presence — `stats.sync.ts`
 * (web-side) already excludes an event this actor can't read stats for, or
 * one with nothing to show, so a missing map entry renders no footer at all.
 */
import { fill, gravity, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { finalize, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import "./event-form.view.ts";
import "./event-sale-progress.view.ts";
import "./event-tile.view.ts";
import {
	canManageEvents,
	eventsListState,
	eventsPageView,
} from "./events.state.ts";
import { eventSaleStats } from "./stats.state.ts";
import { ticketTypesEventId$ } from "./ticket-types/ticket-types.state.ts";

@schmancyElement("event-list")
export class EventList extends LocalizeMixin(SchmancyElement) {
	// Both create and edit dispatch cancelable so a host with a real route
	// (web's events.view.ts) can preventDefault() and reroute instead of this
	// overlay — create to a full-screen create page, edit to the fully
	// emancipated event console; an unlistened dispatch (the studio's flat
	// demo area) leaves defaultPrevented false and falls through unchanged.
	private openForm(id?: string): void {
		if (id === undefined) {
			if (!canManageEvents.get()) return;
			const createEvent = new CustomEvent("create-event", {
				bubbles: true,
				composed: true,
				cancelable: true,
			});
			this.dispatchEvent(createEvent);
			if (createEvent.defaultPrevented) return;
		} else {
			const openEvent = new CustomEvent("open-event", {
				detail: { id },
				bubbles: true,
				composed: true,
				cancelable: true,
			});
			this.dispatchEvent(openEvent);
			if (openEvent.defaultPrevented) return;
			// Opening an event is not itself a manage act: web takes this over and
			// routes to the event console, which is open to a reports- or scan-only
			// actor too (admin-shell.view.ts's own event-console-view gate). Only
			// the unrouted fallback below — the edit overlay — is a manage surface,
			// so the grant is checked here rather than on the way in, where it
			// would strand exactly the actor the list was opened for.
			if (!canManageEvents.get()) return;
			// event-form.view.ts never touches ticketTypesEventId$ itself (the
			// console owns it for the console's Edit destination) — this overlay
			// is event-form's one genuine standalone mount, so it owns the
			// channel for exactly this overlay's own lifetime.
			ticketTypesEventId$.next(id);
		}
		show(() => html`<event-form .id=${id ?? ""}></event-form>`)
			.pipe(
				finalize(() => {
					if (id !== undefined) ticketTypesEventId$.next(null);
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	override render(): TemplateResult {
		const { rows } = eventsPageView.get();
		const canManage = canManageEvents.get();

		return html`
			<section ${fill()} class="flex flex-col">
				<schmancy-surface
					${gravity({ distance: 18, mass: 1.1 })}
					type="container"
					class="block shrink-0 rounded-b-2xl px-3 py-3 md:px-6 md:py-5"
				>
					<div class="flex items-center justify-between gap-3">
						<div class="flex items-baseline gap-2 flex-wrap flex-1 min-w-0">
							<schmancy-typography type="display" token="" class="tabular-nums text-2xl md:text-4xl">
								${rows.length}
							</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t(
									rows.length === 1
										? "admin.events.list.countSingular"
										: "admin.events.list.countPlural",
								)}
							</schmancy-typography>
						</div>
						${when(
							canManage,
							() => html`
								<schmancy-icon-button icon="add" aria-label=${this.t("admin.events.list.addAriaLabel")} @click=${() => this.openForm()}></schmancy-icon-button>
							`,
						)}
					</div>
				</schmancy-surface>

				<div ${overflowWithin()} class="min-h-0">
					${choose(eventsListState.get(), [
						[
							"loading",
							() => html`
								<div
									role="status"
									aria-busy="true"
									aria-label=${this.t("admin.events.list.loadingAriaLabel")}
									class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 px-4 md:px-6 pb-6 pt-4"
								>
									${repeat(
										[0, 1, 2, 3, 4] as const,
										(i) => i,
										() => html`
											<div class="flex flex-col gap-2">
												<schmancy-skeleton class="aspect-[2/3] w-full rounded-2xl"></schmancy-skeleton>
												<schmancy-skeleton shape="text" class="w-3/4"></schmancy-skeleton>
												<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
											</div>
										`,
									)}
								</div>
							`,
						],
						[
							"error",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-error-default">error</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.events.list.errorTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.events.list.errorBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"denied",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-error-default">lock</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.events.list.deniedTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.events.list.deniedBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"empty",
							() => html`
								<div class="flex flex-col items-start gap-3 px-6 py-8">
									<schmancy-icon class="text-3xl text-surface-onVariant">event</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.events.list.emptyTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.events.list.emptyBody")}
									</schmancy-typography>
									${when(
										canManage,
										() => html`
											<schmancy-button variant="filled" @click=${() => this.openForm()}>
												<schmancy-icon slot="prefix" aria-hidden="true">add</schmancy-icon>
												${this.t("admin.events.list.addFirst")}
											</schmancy-button>
										`,
									)}
								</div>
							`,
						],
						[
							"populated",
							() => html`
								<div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6 px-4 md:px-6 pb-6 pt-4">
									${repeat(
										rows,
										(r) => r.id,
										(r) => html`
											<event-tile
												.row=${r}
												.posterCarriesSchedule=${true}
												@open=${(e: CustomEvent<{ id: string }>) => this.openForm(e.detail.id)}
											>
												${when(
													eventSaleStats.value.get(r.id),
													(stats) => html`
														<event-sale-progress slot="footer" .stats=${stats}></event-sale-progress>
													`,
												)}
											</event-tile>
										`,
									)}
								</div>
							`,
						],
					])}
				</div>
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-list": EventList;
	}
}
